import React, { useEffect, useRef, useState } from "react";
import {
    Drawer,
    Form,
    Row,
    Col,
    Select,
    Input,
    InputNumber,
    DatePicker,
    TimePicker,
    Button,
    message,
    Table,
    Space,
    Spin,
    Tag,
} from "antd";
import Column from "antd/es/table/Column";
import dayjs, { Dayjs } from "dayjs";
import { GlobalOutlined } from "@ant-design/icons";
import { useFetchTravellerList } from "../services/traveller.service";
import { useFetchCountry } from "../services/countries.service";
import { useFetchStates } from "../services/states.service";
import { useFetchCities } from "../services/cities.service";
import {
    useCreateTripWithSchedule,
    useGetAvailableAttendants,
    useSelectAgent,
    useAdminSettlePaymentAndAssign,
} from "../services/trip.service";
import { isNotNullUndefined, UseDebounce } from "../helpers/common";
import Pagination from "../helpers/pagination";

type CreateTripDrawerProps = {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
};

const CreateTripDrawer: React.FC<CreateTripDrawerProps> = ({ open, onClose, onCreated }) => {
    const [form] = Form.useForm();
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [submitting, setSubmitting] = useState(false);

    // Step 1 -- traveller search
    const [travellerSearch, setTravellerSearch] = useState("");
    const debouncedTravellerSearch = UseDebounce(travellerSearch, 400);
    const [travellerOptions, setTravellerOptions] = useState<any[]>([]);

    // Step 1 -- country/state/city
    const [countries, setCountries] = useState<any[]>([]);
    const [stateOptions, setStateOptions] = useState<any[]>([]);
    const [cityOptions, setCityOptions] = useState<any[]>([]);

    // Step 2 -- created trip + agent picker
    const [createdTrip, setCreatedTrip] = useState<{ tripId: string; scheduleId: string; referenceId: string; cityId: number } | null>(null);
    const [agentList, setAgentList] = useState<any[]>([]);
    const [agentSearch, setAgentSearch] = useState("");
    const debouncedAgentSearch = UseDebounce(agentSearch, 400);
    const [agentLoading, setAgentLoading] = useState(false);
    const pageNumber = useRef(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (open) {
            resetAll();
            fetchCountryMutation.mutate({ searchFilter: "", isServiceableAttendant: "", isServiceableTraveler: "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;
        fetchTravellerListMutation.mutate({
            travellerId: "",
            statusFilter: "All",
            searchFilter: debouncedTravellerSearch,
            pageNumber: 1,
            pageSize: 20,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedTravellerSearch, open]);

    useEffect(() => {
        if (step === 2 && createdTrip) {
            fetchAgentList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedAgentSearch, step]);

    const resetAll = () => {
        form.resetFields();
        setStep(1);
        setTravellerSearch("");
        setTravellerOptions([]);
        setStateOptions([]);
        setCityOptions([]);
        setCreatedTrip(null);
        setAgentList([]);
        setAgentSearch("");
        pageNumber.current = 1;
    };

    const fetchTravellerListMutation = useFetchTravellerList({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200 && isNotNullUndefined(res.data)) {
                setTravellerOptions(res.data);
            } else {
                setTravellerOptions([]);
            }
        },
        onError: () => setTravellerOptions([]),
    });

    const fetchCountryMutation = useFetchCountry({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response)) setCountries(response);
        },
        onError: (error: any) => message.error(error?.message),
    });

    const fetchStateMutation = useFetchStates({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response)) setStateOptions(response.data);
        },
        onError: (error: any) => message.error(error?.message),
    });

    const fetchCityMutation = useFetchCities({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response)) setCityOptions(response.data);
        },
        onError: (error: any) => message.error(error?.message),
    });

    const onCountryChange = (countryId: number) => {
        setStateOptions([]);
        setCityOptions([]);
        form.setFieldValue("state", null);
        form.setFieldValue("city", null);
        if (countryId) {
            fetchStateMutation.mutate({ countryId, searchFilter: "" });
        }
    };

    const onStateChange = (stateId: number) => {
        setCityOptions([]);
        form.setFieldValue("city", null);
        const countryId = form.getFieldValue("country");
        if (stateId && countryId) {
            fetchCityMutation.mutate({
                stateId,
                countryId,
                searchFilter: "",
                isFavourite: "",
                isServiceableAttendant: "",
                isServiceableTraveler: "",
            });
        }
    };

    const createTripMutation = useCreateTripWithSchedule({
        onSuccess: (res: any) => {
            setSubmitting(false);
            if (res?.statusCode !== 200 && res?.statusCode !== 201) {
                message.error(res?.statusMessage || "Could not create the trip.");
                return;
            }
            const cityId = form.getFieldValue("city");
            setCreatedTrip({
                tripId: res.data.tripId,
                scheduleId: res.data.scheduleId,
                referenceId: res.data.referenceId,
                cityId,
            });
            message.success("Trip created -- now select an attendant.");
            setStep(2);
        },
        onError: (err: any) => {
            setSubmitting(false);
            message.error(err?.response?.statusMessage || "Could not create the trip.");
        },
    });

    const onFinishStep1 = (values: any) => {
        const fromTime: Dayjs = values.fromTime;
        const toTime = fromTime.add(values.hours, "hour");
        const referenceId = crypto.randomUUID();

        setSubmitting(true);
        createTripMutation.mutate({
            travellerId: values.travellerId,
            countryId: values.country,
            stateId: values.state,
            cityId: values.city,
            noOfPax: values.noOfPax,
            referenceId,
            pickupAddress: values.pickupAddress,
            scheduleDate: values.scheduleDate.format("YYYY-MM-DD"),
            hours: values.hours,
            fromTime: fromTime.format("HH:mm:ss"),
            toTime: toTime.format("HH:mm:ss"),
        });
    };

    const fetchAgentList = () => {
        if (!createdTrip) return;
        setAgentLoading(true);
        getAvailableAttendantsMutation.mutate({
            tripId: createdTrip.tripId,
            referenceId: createdTrip.referenceId,
            pageNumber: pageNumber.current,
            pageSize,
            searchFilter: debouncedAgentSearch,
        });
    };

    const getAvailableAttendantsMutation = useGetAvailableAttendants({
        onSuccess: (res: any) => {
            setAgentLoading(false);
            if (res?.statusCode === 200 && isNotNullUndefined(res.data)) {
                const availability = res.data.agentAvailability || [];
                setAgentList(availability);
                setTotalPages(Math.ceil((availability[0]?.recordCount ?? 0) / pageSize) || 1);
            } else {
                setAgentList([]);
            }
        },
        onError: () => {
            setAgentLoading(false);
            setAgentList([]);
        },
    });

    const selectAgentMutation = useSelectAgent({
        onSuccess: (res: any) => {
            setSubmitting(false);
            if (res?.statusCode !== 200 && res?.statusCode !== 201) {
                message.error(res?.statusMessage || "Could not assign that attendant.");
                return;
            }
            message.success("Attendant assigned -- now settle the platform fee to make the trip live.");
            setStep(3);
        },
        onError: (err: any) => {
            setSubmitting(false);
            message.error(err?.response?.statusMessage || "Could not assign that attendant.");
        },
    });

    const handleSelectAgent = (agentId: string) => {
        if (!createdTrip) return;
        setSubmitting(true);
        selectAgentMutation.mutate({
            tripId: createdTrip.tripId,
            agentId,
            cityId: createdTrip.cityId,
            referenceId: createdTrip.referenceId,
        });
    };

    const [settlingOption, setSettlingOption] = useState<"WAIVE_ALL" | "PLATFORM_FEE_ONLY" | "CHARGE_BOTH" | null>(null);

    const settlePaymentAndAssignMutation = useAdminSettlePaymentAndAssign({
        onSuccess: (res: any) => {
            setSubmitting(false);
            setSettlingOption(null);
            if (res?.statusCode !== 200 && res?.statusCode !== 201) {
                message.error(res?.statusMessage || "Could not settle payment for this trip.");
                return;
            }
            message.success("Trip assigned and is now live for the attendant -- no separate acceptance needed.");
            onCreated();
            onClose();
        },
        onError: (err: any) => {
            setSubmitting(false);
            setSettlingOption(null);
            message.error(err?.response?.statusMessage || "Could not settle payment for this trip.");
        },
    });

    const handleSettlePaymentAndAssign = (paymentOption: "WAIVE_ALL" | "PLATFORM_FEE_ONLY" | "CHARGE_BOTH") => {
        if (!createdTrip) return;
        setSubmitting(true);
        setSettlingOption(paymentOption);
        settlePaymentAndAssignMutation.mutate({
            tripId: createdTrip.tripId,
            scheduleId: createdTrip.scheduleId,
            paymentOption,
        });
    };

    return (
        <Drawer
            title={
                <div className="text-xl font-semibold">
                    {step === 1 ? "Create Trip" : step === 2 ? "Select Attendant" : "Settle Payment"}
                </div>
            }
            placement="right"
            open={open}
            onClose={onClose}
            width="45%"
            styles={{ body: { paddingBottom: 80 } }}
            footer={
                step === 1 ? (
                    <div className="flex flex-row gap-2 justify-end">
                        <Button onClick={onClose} size="large" disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => form.submit()}
                            type="primary"
                            size="large"
                            loading={submitting}
                            className="bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black"
                        >
                            Create Trip
                        </Button>
                    </div>
                ) : step === 2 ? (
                    <div className="flex flex-row gap-2 justify-end">
                        <Button onClick={onClose} size="large" disabled={submitting}>
                            Close (assign later)
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-row gap-2 justify-end">
                        <Button onClick={onClose} size="large" disabled={submitting}>
                            Close (settle payment later)
                        </Button>
                    </div>
                )
            }
        >
            {step === 1 && (
                <Form layout="vertical" form={form} onFinish={onFinishStep1}>
                    <Form.Item
                        name="travellerId"
                        label="Traveller"
                        rules={[{ required: true, message: "Please select a traveller" }]}
                    >
                        <Select
                            showSearch
                            size="large"
                            placeholder="Search traveller by name, email, or phone"
                            filterOption={false}
                            onSearch={setTravellerSearch}
                            notFoundContent={fetchTravellerListMutation.isLoading ? <Spin size="small" /> : null}
                            options={travellerOptions.map((t: any) => ({
                                label: `${t.fullName} (${t.email || t.phoneNo || ""})`,
                                value: t.travellerId,
                            }))}
                            getPopupContainer={(trigger) => trigger.parentElement}
                        />
                    </Form.Item>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item name="country" label="Country" rules={[{ required: true, message: "Please select country" }]}>
                                <Select
                                    showSearch
                                    size="large"
                                    placeholder="Select country"
                                    optionFilterProp="label"
                                    options={countries.map((x: any) => ({ label: x.countryName, value: x.countryId }))}
                                    onChange={onCountryChange}
                                    suffixIcon={<GlobalOutlined className="text-gray-400" />}
                                    getPopupContainer={(trigger) => trigger.parentElement}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="state" label="State" rules={[{ required: true, message: "Please select state" }]}>
                                <Select
                                    showSearch
                                    size="large"
                                    placeholder="Select state"
                                    optionFilterProp="label"
                                    options={stateOptions.map((x: any) => ({ label: x.stateName, value: x.stateId }))}
                                    onChange={onStateChange}
                                    getPopupContainer={(trigger) => trigger.parentElement}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="city" label="City" rules={[{ required: true, message: "Please select city" }]}>
                                <Select
                                    showSearch
                                    size="large"
                                    placeholder="Select city"
                                    optionFilterProp="label"
                                    options={cityOptions.map((x: any) => ({ label: x.cityName, value: x.cityId }))}
                                    getPopupContainer={(trigger) => trigger.parentElement}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item name="scheduleDate" label="Pickup Date" rules={[{ required: true, message: "Please select a date" }]}>
                                <DatePicker size="large" style={{ width: "100%" }} disabledDate={(d) => d.isBefore(dayjs().startOf("day"))} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="fromTime" label="Pickup Time" rules={[{ required: true, message: "Please select a time" }]}>
                                <TimePicker size="large" style={{ width: "100%" }} use12Hours format="h:mm A" minuteStep={15} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="hours" label="Duration (hours)" rules={[{ required: true, message: "Please enter hours" }]}>
                                <InputNumber size="large" style={{ width: "100%" }} min={1} max={24} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={16}>
                            <Form.Item
                                name="pickupAddress"
                                label="Pickup Location"
                                rules={[{ required: true, message: "Please enter a pickup address" }]}
                            >
                                <Input size="large" placeholder="Enter pickup address" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="noOfPax"
                                label="Number of People"
                                rules={[{ required: true, message: "Please enter number of people" }]}
                            >
                                <InputNumber size="large" style={{ width: "100%" }} min={1} max={20} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}

            {step === 2 && (
                <div>
                    <div className="mb-4">
                        <Input.Search
                            placeholder="Search attendant"
                            size="large"
                            allowClear
                            onChange={(e) => setAgentSearch(e.target.value)}
                        />
                    </div>
                    <Table
                        dataSource={agentList}
                        rowKey="agentId"
                        bordered
                        loading={agentLoading}
                        pagination={{ pageSize, position: ["none"] }}
                    >
                        <Column title="Attendant Name" dataIndex="agentName" key="agentName" align="center" />
                        <Column title="City" dataIndex="agentCityName" key="agentCityName" align="center" />
                        <Column
                            title="Hourly Rate"
                            dataIndex="hourlyRate"
                            key="hourlyRate"
                            align="center"
                            render={(rate: number) => `$${rate}`}
                        />
                        <Column
                            title="Rating"
                            dataIndex="agentRating"
                            key="agentRating"
                            align="center"
                            render={(rating: number) => (
                                <Tag color={rating >= 4 ? "green" : rating >= 3 ? "blue" : rating > 0 ? "orange" : "default"}>
                                    {rating > 0 ? `${rating.toFixed(1)} ⭐` : "No Rating"}
                                </Tag>
                            )}
                        />
                        <Column
                            title="Action"
                            key="action"
                            align="center"
                            render={(_: any, record: any) => (
                                <Space>
                                    <Button
                                        className="bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black"
                                        loading={submitting}
                                        onClick={() => handleSelectAgent(record.agentId)}
                                    >
                                        Assign
                                    </Button>
                                </Space>
                            )}
                        />
                    </Table>
                    <Pagination
                        currentPage={pageNumber.current}
                        totalPages={totalPages}
                        onPageChange={(page: number) => {
                            pageNumber.current = page;
                            fetchAgentList();
                        }}
                    />
                </div>
            )}

            {step === 3 && (
                <div>
                    <p className="mb-4 text-gray-600">
                        An attendant has been assigned. Choose how this booking was paid for -- this also
                        finalizes the assignment, so the attendant sees it as an upcoming trip right away and
                        doesn't need to separately accept it.
                    </p>
                    <div className="flex flex-col gap-3">
                        <Button
                            size="large"
                            className="bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black"
                            loading={settlingOption === "CHARGE_BOTH" && settlePaymentAndAssignMutation.isLoading}
                            disabled={submitting && settlingOption !== "CHARGE_BOTH"}
                            onClick={() => handleSettlePaymentAndAssign("CHARGE_BOTH")}
                        >
                            Charge Platform Fee and Service Charge
                        </Button>
                        <Button
                            size="large"
                            loading={settlingOption === "PLATFORM_FEE_ONLY" && settlePaymentAndAssignMutation.isLoading}
                            disabled={submitting && settlingOption !== "PLATFORM_FEE_ONLY"}
                            onClick={() => handleSettlePaymentAndAssign("PLATFORM_FEE_ONLY")}
                        >
                            Charge Platform Fee Only
                        </Button>
                        <Button
                            size="large"
                            loading={settlingOption === "WAIVE_ALL" && settlePaymentAndAssignMutation.isLoading}
                            disabled={submitting && settlingOption !== "WAIVE_ALL"}
                            onClick={() => handleSettlePaymentAndAssign("WAIVE_ALL")}
                        >
                            Customer Already Paid (Waive All Fees)
                        </Button>
                    </div>
                </div>
            )}
        </Drawer>
    );
};

export default CreateTripDrawer;
